import { DateTime, DurationUnit } from 'luxon';
import { Log } from '../../AuxillaryService/Log';
import { ReachAPI } from '../../ReachApi';
import { Signage } from '../../Signage';
import { TemplateWidgetMixin } from '../../Template';
import { Handlebars } from '../../Utility/Handlebars/databaseHandlebars';
import { WidgetState, WidgetType } from '../types';
import { WidgetWithScheduler } from '../WidgetWithScheduler';
import {
    CounterAmPmEnum,
    CounterDateObject,
    CounterDateRowEntry,
    CounterDateTypeEnum,
    CounterDirectionEnum,
    CounterTypeEnum,
    CounterWidgetConfiguration,
    CounterWidgetConfigurationDetails,
    CounterWidgetTemplateData,
    CounterWidgetTimeProperties,
    ZodCounterWidgetConfigurationDetails,
    ZodCounterWidgetDate,
    ZodCounterWidgetDateTime,
    ZodCounterWidgetTime,
} from './types';

export class CounterWidget extends TemplateWidgetMixin(WidgetWithScheduler) {
    readonly type: WidgetType.COUNTER = WidgetType.COUNTER;
    declare readonly config: CounterWidgetConfigurationDetails;

    /**
     * DateTime to reference when counting up or down.
     */
    #dateTime = DateTime.local();

    /**
     * Compiled handlebars function handler
     */
    #handlebarsTemplate?: HandlebarsTemplateDelegate;

    /**
     * Whether counter is before/after countup/countdown
     */
    #notExpired = false;

    /**
     * Array holding data list and calendar dates if applicable
     */
    #counterDates: CounterDateRowEntry[] = [];

    /**
     * The actual string displayed to the browser.
     */
    #countDownResult: string | undefined;

    /**
     * Contains a list of strings from inside the `{{}}` of the handlebars template.
     */
    #handlebarsTemplateItems: string[];

    constructor(widgetConfiguration: CounterWidgetConfiguration, zoneId: number) {
        super(widgetConfiguration, zoneId);

        this.#handlebarsTemplateItems = this.getHandlebarTemplateStringItems(
            this.config.handlebars
        );

        const secondsUsed =
            this.handlebarTemplateItemsContains('s') || this.handlebarTemplateItemsContains('ss');

        this.#handlebarsTemplate = Handlebars.compile(this.config.handlebars);
        this.schedulerRunInterval = secondsUsed ? 1 : 60;
    }

    protected validateConfigurationDetails(
        maybeCounterWidgetConfigurationDetails: CounterWidgetConfigurationDetails
    ): CounterWidgetConfigurationDetails {
        return ZodCounterWidgetConfigurationDetails.parse(maybeCounterWidgetConfigurationDetails);
    }

    /**
     * Adds zero to beginning if needed
     */
    prependZero(digit: number): string {
        if (digit.toString().length > 1) {
            return digit.toString();
        } else {
            return '0' + digit.toString();
        }
    }

    /**
     * Action to populate counter text
     */
    async schedulerAction(): Promise<void> {
        // do not count unless the widget is playing and is not the first time
        // playing
        if (this.state !== WidgetState.PLAYING) {
            return;
        }

        try {
            await this.updateCountdownResult();
            await this.renderContent();
        } catch (err) {
            Log.Error(
                `Failed to render widget content (${this.id}:${this.name})`,
                null,
                __fileline,
                err
            );
        }
    }

    /**
     * Update the value that will be rendered.
     */
    async updateCountdownResult(): Promise<void> {
        this.#notExpired = false;
        const timeZoneName =
            this.config && this.config.timezone && this.config.timezone !== 'auto'
                ? this.config.timezone
                : Signage.localTimeZone;

        const nowDateTime = DateTime.local().setZone(timeZoneName);

        let countdownData: CounterWidgetTimeProperties = {};

        this.hasContentToPlay = this.#counterDates.length > 0;

        if (this.#counterDates.length > 0) {
            countdownData.title = this.updateTitleByClosestDate(nowDateTime);

            if (!this.#notExpired && this.config.loop) {
                countdownData.title = this.updateTitleByLoopingDate();
            }
        }

        if (this.#dateTime) {
            countdownData = {
                ...countdownData,
                ...this.populateCountdownTimeData(nowDateTime),
            };
        }

        if (this.#notExpired && this.#handlebarsTemplate) {
            this.#countDownResult = this.#handlebarsTemplate(countdownData);
        } else {
            this.#countDownResult = this.config.expiration_text;
        }
    }

    /**
     * Creates the rendered item data.
     * Some templates use jquery to modify the display data,
     *   adding commas.
     */
    protected getRenderContext(): CounterWidgetTemplateData {
        return {
            counterText: this.#countDownResult || '',
            Widget: { Id: this.id },
        };
    }

    /**
     * Uses widget config or data list to retrieve array of dates to count to
     */
    async getDateList(): Promise<void> {
        if (this.config.counter_type.toLowerCase() !== CounterTypeEnum.MANUAL) {
            throw new Error(`Invalid counter_type: ${this.config.counter_type}`);
        }

        // TODO: document return type.
        const data = await ReachAPI.signage.getWidgetConfig(this.id);

        if (data?.widget?.config) {
            this.#counterDates = [];

            this.config.date = data.widget.config.date;
            const dateObject = this.createDateObject(data.widget.config.date);

            if (dateObject) {
                this.#counterDates.push({
                    ...dateObject,
                    title: this.config.title,
                });
            }
        }
    }

    /**
     * @inheritdoc
     * @description Update the date list, populate the display results, and
     *   render the updated content.
     */
    async getReadyForDisplay(): Promise<void> {
        try {
            await this.getDateList();
            await this.updateCountdownResult();
            await this.renderContent();
        } catch (err) {
            // ignore error and run scheduler anyway.
            Log.Error('Some error happened getting DateList', null, __fileline, err);
        }
    }

    /**
     * Create a CounterDateObject from a json date string.
     * @example createDateObject('{"month":"Jan","date":"4","year":"2022"}')
     */
    createDateObject(jsonDate: string): CounterDateObject | undefined {
        const includesDate = jsonDate.includes('month');
        const includesTime = jsonDate.includes('hour');
        let newDateObject: CounterDateObject | undefined;

        try {
            /**
             * There is an edge case for DateTime && Date, where in the original it
             *   would set the type to `date` if there is no
             *   year value, so it can "recycle" or loop the date.
             * TLDR: If Recycle is set a "year" value should not be set.
             */
            if (includesDate && includesTime) {
                const zodParsedDateTime = ZodCounterWidgetDateTime.parse(JSON.parse(jsonDate));

                newDateObject = {
                    value: zodParsedDateTime,
                    date: DateTime.local().set({
                        day: zodParsedDateTime.date,
                        month: DateTime.fromFormat(zodParsedDateTime.month, 'MMM').month,
                        year: zodParsedDateTime.year || undefined,
                        hour:
                            zodParsedDateTime.ampm === CounterAmPmEnum.PM
                                ? zodParsedDateTime.hour + 12
                                : zodParsedDateTime.hour,
                        minute: zodParsedDateTime.minute,
                    }),
                    type: zodParsedDateTime.year
                        ? CounterDateTypeEnum.DATE_TIME
                        : CounterDateTypeEnum.DATE,
                };
            } else if (includesDate) {
                const zodParsedDate = ZodCounterWidgetDate.parse(JSON.parse(jsonDate));

                newDateObject = {
                    value: zodParsedDate,
                    date: DateTime.local().set({
                        day: zodParsedDate.date,
                        month: DateTime.fromFormat(zodParsedDate.month, 'MMM').month,
                        year: zodParsedDate.year || undefined,
                        // Default the start time to end of the day when counting down
                        //   and start of the day when counting up.
                        hour: this.config.direction === CounterDirectionEnum.DOWN ? 23 : 0,
                        minute: this.config.direction === CounterDirectionEnum.DOWN ? 59 : 0,
                        second: this.config.direction === CounterDirectionEnum.DOWN ? 59 : 0,
                    }),
                    type: zodParsedDate.year
                        ? CounterDateTypeEnum.DATE_TIME
                        : CounterDateTypeEnum.DATE,
                };
            } else if (includesTime) {
                const zodParsedTime = ZodCounterWidgetTime.parse(JSON.parse(jsonDate));

                newDateObject = {
                    value: zodParsedTime,
                    date: DateTime.local().set({
                        hour:
                            zodParsedTime.ampm === CounterAmPmEnum.PM
                                ? zodParsedTime.hour + 12
                                : zodParsedTime.hour,
                        minute: zodParsedTime.minute,
                    }),
                    type: CounterDateTypeEnum.TIME,
                };
            }

            return newDateObject;
        } catch (err) {
            Log.Error('Unable to parse date object.', null, __fileline, err);
            throw err;
        }
    }

    /**
     * Return an array of valid durationUnits for DateTime diff
     *   that are valid handlebarTemplateItems.
     */
    getDateTimeDiffTypes(): DurationUnit | DurationUnit[] {
        const types: DurationUnit[] = [];

        this.#handlebarsTemplateItems.forEach((templateItem) => {
            switch (templateItem) {
                case 'y':
                    types.push('years');
                    break;
                case 'mo':
                    types.push('months');
                    break;
                case 'w':
                    types.push('weeks');
                    break;
                case 'd':
                    types.push('days');
                    break;
                case 'h':
                case 'hh':
                    types.push('hours');
                    break;
                case 'm':
                case 'mm':
                    types.push('minutes');
                    break;
                case 's':
                case 'ss':
                    types.push('seconds');
                    break;
                default:
                    break;
            }
        });

        return types;
    }

    /**
     * Based off of time difference passed in update the
     *   current dateTime value and return rounded down number.
     */
    private getTimeDifferenceByType(
        handlebarValue: DurationUnit,
        timeDifference: number | undefined
    ): number {
        let returnNumber = 0;

        if (typeof timeDifference !== 'undefined' && timeDifference > 0) {
            if (this.config.direction === CounterDirectionEnum.DOWN) {
                this.#dateTime.minus({ [handlebarValue]: timeDifference });
            } else {
                this.#dateTime.plus({ [handlebarValue]: timeDifference });
            }
            returnNumber = Math.floor(timeDifference);
        }

        return returnNumber;
    }

    /**
     * Convert the handlebar template into an array of params used in the template.
     * @example '{{hh}}:{{mm}}:{{ss}}' => ['hh', 'mm', 'ss']
     */
    private getHandlebarTemplateStringItems(handlebarTemplate: string): string[] {
        const regex = new RegExp(/{{([a-z]*)}}/g);
        const combinedResults: string[] = [];

        let foundResult: RegExpExecArray | null = null;
        do {
            foundResult = regex.exec(handlebarTemplate);
            if (foundResult && foundResult[1]) {
                combinedResults.push(foundResult[1]);
            }
        } while (foundResult);

        return combinedResults;
    }

    /**
     * Check if the handlebar template items contains a string.
     */
    private handlebarTemplateItemsContains(templateItemString: string): string | undefined {
        return this.#handlebarsTemplateItems.find((templateItem) => {
            return templateItem === templateItemString;
        });
    }

    /**
     * Find the closest valid date from counterDates, return the title, and
     *   set the updated dateTime and notExpired values.
     */
    private updateTitleByClosestDate(nowDateTime: DateTime): string | undefined {
        let newCountdownDataTitle: string | undefined;
        for (const counterDate of this.#counterDates) {
            if (counterDate.date) {
                const testDateTime = DateTime.fromMillis(counterDate.date.toMillis());

                if (testDateTime.isValid) {
                    //figure out which date is closest
                    const testDateDifference =
                        this.config.direction === CounterDirectionEnum.DOWN
                            ? testDateTime.diff(nowDateTime)
                            : nowDateTime.diff(testDateTime);
                    if (testDateDifference.milliseconds > 0) {
                        newCountdownDataTitle = counterDate.title;
                        this.#dateTime = testDateTime;
                        this.#notExpired = true;
                        break;
                    }
                }
            }
        }

        return newCountdownDataTitle;
    }

    /**
     * When the counter has looping set add a year or day to the value
     *   that will be displayed so it will repeat.
     */
    private updateTitleByLoopingDate(): string | undefined {
        let newCountdownDataTitle: string | undefined;
        if (this.#counterDates[0]) {
            const firstCounterDate = this.#counterDates[0];
            if (firstCounterDate.type && firstCounterDate.date) {
                const dateTimeCopy = DateTime.fromMillis(firstCounterDate.date.toMillis());

                if (firstCounterDate.type.toLowerCase() === CounterDateTypeEnum.DATE) {
                    //just date, add a year to first item
                    this.#dateTime = dateTimeCopy.plus({ years: 1 });
                } else if (firstCounterDate.type.toLowerCase() === CounterDateTypeEnum.TIME) {
                    //just time, add day to first item
                    this.#dateTime = dateTimeCopy.plus({ days: 1 });
                }

                if (firstCounterDate.type !== CounterDateTypeEnum.DATE_TIME) {
                    this.#notExpired = true;
                    newCountdownDataTitle = firstCounterDate.title;
                }
            }
        }

        return newCountdownDataTitle;
    }

    /**
     * Populate the countdown time handlebars data.
     */
    private populateCountdownTimeData(nowDateTime: DateTime): CounterWidgetTimeProperties {
        const countdownData: CounterWidgetTimeProperties = {};
        const timeDiffTypes = this.getDateTimeDiffTypes();
        const overallTimeDifference =
            this.config.direction === CounterDirectionEnum.DOWN
                ? this.#dateTime.diff(nowDateTime, timeDiffTypes).toObject()
                : nowDateTime.diff(this.#dateTime, timeDiffTypes).toObject();

        // Set the countdownData number values first so they can be
        //   used to populate the lists if set.
        this.#handlebarsTemplateItems
            .filter((item) => !item.includes('l'))
            .forEach((handlebarTemplateItem) => {
                switch (handlebarTemplateItem) {
                    case 'y':
                        countdownData.y = this.getTimeDifferenceByType(
                            'years',
                            overallTimeDifference.years
                        );
                        break;
                    case 'mo':
                        countdownData.mo = this.getTimeDifferenceByType(
                            'months',
                            overallTimeDifference.months
                        );
                        break;
                    case 'w':
                        countdownData.w = this.getTimeDifferenceByType(
                            'weeks',
                            overallTimeDifference.weeks
                        );
                        break;
                    case 'd':
                        countdownData.d = this.getTimeDifferenceByType(
                            'days',
                            overallTimeDifference.days
                        );
                        break;
                    case 'h':
                    case 'hh':
                        countdownData.h = this.getTimeDifferenceByType(
                            'hours',
                            overallTimeDifference.hours
                        );
                        countdownData.hh = this.prependZero(countdownData.h);
                        break;
                    case 'm':
                    case 'mm':
                        countdownData.m = this.getTimeDifferenceByType(
                            'minutes',
                            overallTimeDifference.minutes
                        );
                        countdownData.mm = this.prependZero(countdownData.m);
                        break;
                    case 's':
                    case 'ss':
                        countdownData.s = this.getTimeDifferenceByType(
                            'seconds',
                            overallTimeDifference.seconds
                        );
                        countdownData.ss = this.prependZero(countdownData.s);
                        break;
                }
            });

        // Set up the labels if configured and using the numbers see if they
        //   are required to be shown.
        this.#handlebarsTemplateItems
            .filter((item) => item.includes('l'))
            .forEach((handlebarTemplateItem) => {
                switch (handlebarTemplateItem) {
                    case 'sl':
                        if (typeof countdownData.s !== 'undefined' && countdownData.s >= 0) {
                            countdownData.sl = countdownData.s === 1 ? 'second' : 'seconds';
                        }
                        break;
                    case 'ml':
                        if (typeof countdownData.m !== 'undefined' && countdownData.m >= 0) {
                            countdownData.ml = countdownData.m === 1 ? 'minute' : 'minutes';
                        }
                        break;
                    case 'hl':
                        if (typeof countdownData.h !== 'undefined' && countdownData.h >= 0) {
                            countdownData.hl = countdownData.h === 1 ? 'hour' : 'hours';
                        }
                        break;
                    case 'dl':
                        if (typeof countdownData.d !== 'undefined' && countdownData.d >= 0) {
                            countdownData.dl = countdownData.d === 1 ? 'day' : 'days';
                        }
                        break;
                    case 'wl':
                        if (typeof countdownData.w !== 'undefined' && countdownData.w >= 0) {
                            countdownData.wl = countdownData.w === 1 ? 'week' : 'weeks';
                        }
                        break;
                    case 'mol':
                        if (typeof countdownData.mo !== 'undefined' && countdownData.mo >= 0) {
                            countdownData.mol = countdownData.mo === 1 ? 'month' : 'months';
                        }
                        break;
                    case 'yl':
                        if (typeof countdownData.y !== 'undefined' && countdownData.y >= 0) {
                            countdownData.yl = countdownData.y === 1 ? 'year' : 'years';
                        }
                        break;
                }
            });

        return countdownData;
    }
}
